import cn from "classnames"
import { lazy, Suspense, useEffect, useState } from "react"
import { useLocation } from "react-router-dom"

import { Divider, Link, LogoImage, Menu, type ItemType } from "@/atoms"
import { isSelfHosted } from "@/helpers/self-hosted.helper"
import { usePrevious } from "@/hooks/usePrevious"
import { useRuntimeCoverage } from "@/hooks/useRuntimeCoverage"
import useServices from "@/hooks/useServices"
import { useTranslation } from "@/hooks/useTranslation"
import { useFeatureFlags, useUser } from "@/hooks/useUser"
import { ApiOutlined, CloudUploadOutlined } from "@/icons"
import { Protected } from "@/molecules/Protected"
import {
  filterItemsByItemKey,
  filterMenuItems,
  getItem,
  getMenuItems
} from "./components/ItemMenu"
import { ScopeSideNavigation } from "./components/ScopeSideNavigation"
import { useKeyByParams } from "./hooks/useKeyByParams"

import styles from "./SideNavigation.module.scss"

import type { SideNavigationComponent } from "."

const AdminCheckListNavigation = lazy(
  () => import("./components/AdminCheckListNavigation")
)
const SelfHostingMetaData = lazy(
  () => import("@/molecules/SelfHostingMetaData")
)

export const SideNavigation: SideNavigationComponent = ({
  collapsed,
  user
}) => {
  const { t } = useTranslation()
  const props = { collapsed, t }
  const { auth } = useServices()
  const { pathname } = useLocation()
  const featureFlags = useFeatureFlags()
  const vmInstancesEnabledFF = featureFlags.vmInstancesEnabled
  const adrIncidentsEnabledFF = featureFlags.adrIncidentsEnabled
  const kortexInstalled = featureFlags.kortexInstalled
  const isInternalTenant = featureFlags.isInternalTenant
  const { isLowRuntimeCoverage } = useRuntimeCoverage()
  const { hasPermission } = useUser()
  const attr = {
    t,
    pathname,
    vmInstancesEnabledFF,
    adrIncidentsEnabledFF,
    kortexInstalled,
    isInternalTenant,
    collapsed,
    isLowRuntimeCoverage,
    isDemo: auth.isDemo(),
    aiDiscovery: featureFlags.aiDiscovery,
    aiPoliciesFF: featureFlags.aiPolicies,
    hasPermission
  }
  const items: ItemType[] = filterMenuItems(getMenuItems(attr), attr)

  const itemKey = useKeyByParams()
  const [openKeys, setOpenKeys] = useState<string[]>(() => {
    if (collapsed) return []

    const result = filterItemsByItemKey(items, itemKey)

    return !result?.length ? ["discovery"] : result
  })
  const prevCollapsed = usePrevious(collapsed)

  useEffect(() => {
    if (prevCollapsed && !collapsed)
      queueMicrotask(() => setOpenKeys(filterItemsByItemKey(items, itemKey)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prevCollapsed, collapsed])

  useEffect(() => {
    if (collapsed) return

    const nextOpenKeys = filterItemsByItemKey(items, itemKey)

    if (!nextOpenKeys.length || openKeys.includes(nextOpenKeys[0])) return

    queueMicrotask(() => {
      setOpenKeys([...openKeys, ...nextOpenKeys])
    })
    // aiDiscovery arrives after the first render, so a direct /ai/* load opens its group once the
    // item exists. It only goes from undefined to a value once per session (re-exchanges return the
    // same value). Edge case, accepted: a group the user collapses before the flag arrives is
    // reopened once when it does.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, featureFlags.aiDiscovery])

  const selectedKeys = [itemKey]
  const menuProps = {
    className: styles.menuNavigationTemplate,
    rootClassName: cn(collapsed && styles.collapsedRootClassName),
    mode: "inline" as const,
    theme: "dark" as const,
    collapsed: `${collapsed}`,
    openKeys,
    selectedKeys
  }
  const selfHosting = isSelfHosted()

  return (
    <div
      className={cn(
        styles.container,
        collapsed ? styles.collapsedContainer : styles.noCollapsedContainer
      )}
    >
      <div className={cn(styles.logo, collapsed && styles.collapsed)}>
        <Link to="/">
          <LogoImage dark height={32} />
        </Link>
        {!selfHosting && (
          <div
            className={cn(
              styles.slugLabel,
              collapsed && styles.slugLabelHidden
            )}
          >
            {user.company}
          </div>
        )}
      </div>
      <div className={styles.section}>
        <ScopeSideNavigation {...props} />
      </div>
      <Divider orientation="horizontal" className={styles.divider} />
      <div className={styles.scrollableSection}>
        <div>
          <Menu
            items={items}
            onOpenChange={(openKeys) => {
              setOpenKeys?.(openKeys)
            }}
            {...menuProps}
          />
        </div>
      </div>
      <Divider orientation="horizontal" className={styles.divider} />
      <div className={styles.section}>
        <Menu
          items={filterMenuItems(
            [
              getItem("Deployments", "/deployments", <CloudUploadOutlined />),
              getItem("Integrations", "/integrations", <ApiOutlined />)
            ],
            attr
          )}
          {...menuProps}
        />
      </div>
      {!selfHosting && (
        <Suspense>
          <Protected permission={{ resource: "system", action: "write" }}>
            <AdminCheckListNavigation {...props} />
          </Protected>
        </Suspense>
      )}
      {selfHosting && (
        <div className={styles.section}>
          <Suspense>
            <SelfHostingMetaData />
          </Suspense>
        </div>
      )}
    </div>
  )
}
